import { useCallback, useContext, useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import pubSub, { EVENTS } from "../../../../config/pubSub";
import { AppConfig } from "../../../../context/context";
import { IOrganizationModel } from "../../../../models/organizations.model";
import { useOrgService } from "../../../../services/organizations-service";
import { useUserService } from "../../../../services/user-service";
import { USER_ROLES } from "../../../../utils/constants";
import Form from "./Form";
import FormActions from "../../../../components/formactions/formactions";
import LeaveRouterPrompt from "../../../../components/leave-confirm-prompt/leave-router-prompt";
import {  validateStep, fieldNames } from "./validate"; 
type IProps = {
  setShowLeavePrompt: (bool: boolean) => void;
};

const GeneralTab = (props: IProps) => {
  const { setShowLeavePrompt } = props;
  const { config } = useContext(AppConfig);
  const [orgRepresentative, setOrgRepresentative] = useState<string>("");
  const [allUsers, setAllUsers] = useState<any>();
  const [orgRepUsers, setOrgRepUsers] = useState<any[]>([]);
  const [hasOrgSettingsChanged, setHasOrgSettingsChanged] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [showBtns, setShowBtns] = useState(false);
  const [validate, setValidate] = useState<any>({});
  const { t } = useTranslation();
  const { orgService } = useOrgService();
  const { userService } = useUserService();
  const ref = useRef<any>(null);
  const [organizationSettings, setOrganizationSettings] = useState<any>()

  const fieldRefs = {
    displayName: useRef(null),
  };

  const fieldErrors = {
    displayName:
      validate.displayName && validate.displayName.touched && validate.displayName.error,
    
  };
  const validateField = (updatedModel: any, fieldName: string) => {
    const _error = validateStep(updatedModel, validate, fieldName);
    if (Object.keys(_error).length) {
      setValidate(_error);
    }

    setOrganizationSettings(updatedModel);
  };

  const handleChange = (event: any) => {
    event.preventDefault();
    const el = event.target;
    const updatedModel = { ...organizationSettings, [el.name]: el.value };


    if (el.name.trim().toLowerCase() === 'displayName') {
      const usernameRegex = /[/\s\s+/g, " "]/gi;
      updatedModel[el.name] = el.value.replace(usernameRegex, '');
    }  
    setHasOrgSettingsChanged(true);
    validateField(updatedModel, el.name);
  };
 
  const usernameToID = (username: string) => {
    const userObj = allUsers.list.filter(
      (user: { username: string }) => user.username === username
    );
    return userObj[0].id;
  };

  const sanitizeModel = (inputModel: any) => {
    const sanitized = { ...inputModel };
    Object.keys(sanitized).forEach((key) => {
      const value = sanitized[key];

      try {
        sanitized[key] =
          typeof value === "string"
            ? value.trim().replace(/\s\s+/g, " ")
            : value;
      } catch (error) {
        console.log(error);
      }
    });
    return sanitized;
  };
  const handleFocusAndBlur = (event: any) => {
    event.preventDefault();
    const el = event.target;
    const cleanUser = sanitizeModel(organizationSettings);
    validateField(cleanUser, el.name);
  };

  const handlePaste = (event: any) => {
    const el = event.target;
    const elName = el.name;
    const updatedModel = { ...organizationSettings };

    switch (elName.trim().toLowerCase()) {
      case "displayName":
        setTimeout(() => {     
          validateField(updatedModel, elName);
        }, 20);
        break;
      
      default:
        break;
    }
  };

  const checkFormValid  = (validates: any) => {
    let touches = false;
    for (const key in validates) {
      if (Object.hasOwnProperty.call(validates, key)) {
        const element = validates[key];
        if (element.error) {
          return false;
        }
        touches = touches || element.touched;
      }
    }
    return touches;
  };

  const initOrgSettings = useCallback(async () => {
    if (!orgService.getOrg) return;
    try {
      const org: IOrganizationModel = await orgService.getOrg(config.tenantId);
      await userService.getUsers().then((allUsers) => {
        setAllUsers(allUsers);
        const orgRep =  allUsers.list.filter(
          (user: { role: string }) => user?.role === USER_ROLES?.admins[0]
        )[0]?.username
        setOrgRepresentative(
          orgRep
        );
        setOrgRepUsers(
          allUsers.list.filter(
            (user: { role: string; activated: boolean }) =>
              (user.role === USER_ROLES.admins[0] ||
                user.role === USER_ROLES.admins[1]) &&
              user.activated
          )
        );
        setOrganizationSettings({displayName: org.displayName, locale:org.locale , representative:  orgRep })
      })
     
    } catch (error) {
      pubSub.publish(EVENTS.ERROR, t("common_error_msg"));
    }
  }, [orgService, config.tenantId]);


  const generalUpdateRequest = async (
    displayName: string,
    emailLanguage: string,
    userRepresentative: string
  ) => {
    await orgService.updateOrg({ orgId: config.tenantId, displayName, locale: emailLanguage });
    await userService.updateUserSetting(usernameToID(userRepresentative), {
      role: USER_ROLES.admins[0],
    });
  };

  const updateOrgSettings = async () => {
    setIsSubmitting(true);
    try {
      const {displayName, locale, representative} = organizationSettings
      generalUpdateRequest(displayName, locale, representative)
        .then(() => setShowBtns(false))
        .then(() => setShowLeavePrompt(false))
        .then(() => {
          pubSub.publish(EVENTS.SUCCESS, t("common_success_updated_msg"));
          pubSub.publish(EVENTS.DISPLAY_NAME_CHANGED, displayName);
        })
    } catch (error: any) {
      pubSub.publish(EVENTS.ERROR, t("common_error_msg"));
    } finally {
      setIsSubmitting(false);
      setHasOrgSettingsChanged(false);
    }   
  };

  const resetOrgSettings = async () => {
    setIsSubmitting(true);
    initOrgSettings()
      .then(() => setShowBtns(false))
      .then(() => setShowLeavePrompt(false));
    try {
    } catch (error: any) {
      pubSub.publish(EVENTS.ERROR, t("common_error_msg"));
    } finally {
      setIsSubmitting(false);
      setShowBtns(true);
      setShowLeavePrompt(true);
      setHasOrgSettingsChanged(false);
      setValidate({})
    }
  };

  useEffect(() => {
    initOrgSettings();
  }, [initOrgSettings]);

  useEffect(() => {
    if (hasOrgSettingsChanged || isSubmitting) {
      setShowBtns(true);
      setShowLeavePrompt(true);
    }
  }, [hasOrgSettingsChanged, isSubmitting]);

  return (
    <>
    {organizationSettings && ( 
    <>
      <LeaveRouterPrompt when={showBtns} />
      <Form
        handleChange={handleChange}
        orgRepUsers={orgRepUsers}
        orgRepresentative={orgRepresentative}
        handleFocusAndBlur = {handleFocusAndBlur}
        fieldErrors={fieldErrors}
        fieldNames = {fieldNames}
        fieldRefs = {fieldRefs}
        validate = {validate}
        handlePaste = {handlePaste}
        organizationSettings={organizationSettings}
        ref = {ref}
      
      />
      <FormActions
        showBtns={showBtns}
        resetSettings={resetOrgSettings}
        updateSettings={updateOrgSettings}
        canSubmit={hasOrgSettingsChanged && checkFormValid(validate)}
      />
    </>
    )}
    </>
  );
};

export default GeneralTab;
